import Callout from "components/callout/callout";
import Section from "components/layout/section";
import SectionHeader from "components/layout/sectionHeader";
import SectionHeaderTitle from "components/layout/sectionHeaderTitle";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductRead } from "types";
import { ReactComponent as CheckBoxIconFilled } from 'assets/icons/check_box.svg'
import { ReactComponent as CheckBoxIconEmpty } from 'assets/icons/check_box_outline_blank.svg'
import useCategories from "hooks/useCategories";
import TableList from "components/list/tableList/tableList";
import TableListRow from "components/list/tableList/tableListRow";
import TableListCell from "components/list/tableList/tableListCell";

interface CustomInputDataDisplayProps {
    storageId: string
    categoryIds?: string[]
    product?: ProductRead
    isLoading?: boolean
}

const CustomInputDataDisplay: React.FC<CustomInputDataDisplayProps> = ({storageId, categoryIds, product, isLoading}) => {

    const { t } = useTranslation()
    const [requiresAttention, setRequiresAttention] = useState(false)

    const customInputs = product?.customInputs

    const {
        allCategories,
        categoriesStatus
    } = useCategories({ storageId })

    // Check if the custom input data is missing.
    useEffect(() => {
    if (!allCategories || !product || !product.categories) return
        // Loop trough the product categories and check if all required custom inputs are filled.
        let requiresAttention = false
        for (const categoryId of product.categories) {
        const category = allCategories.find(c => c.id === categoryId)
            if (!category) continue
            if (!category.customInputs?.customInputGroupOrder || !category.customInputs?.customInputGroups) continue
            for (const customInputGroupId of category.customInputs.customInputGroupOrder) {
                const customInputGroup = category.customInputs.customInputGroups[customInputGroupId]
                if (!customInputGroup.customInputOrder || !customInputGroup.customInputs) continue
                for (const customInputId of customInputGroup.customInputOrder) {
                    const customInput = customInputGroup.customInputs[customInputId]
                    if (customInput.required && (!customInputs || !customInputs[categoryId] || !customInputs[categoryId][customInputId])) {
                        requiresAttention = true
                        break
                    }
                }
            }
        }
        setRequiresAttention(requiresAttention)
    }, [product, storageId, allCategories, customInputs])

    // Catch loading.
    //if (categoriesStatus === `loading` || storage?.categories === undefined) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingCustomData')}</Callout>
    if (categoriesStatus === `loading`) {
        return (
            <div className={`content-inner-two-columns`}>
                <Section>
                    <SectionHeader>
                        <SectionHeaderTitle>
                            <div className="h2_skeleton"></div>
                        </SectionHeaderTitle>
                    </SectionHeader>
                    <TableList>
                        <TableListRow>
                            <TableListCell><p className={`skeleton`}></p></TableListCell>
                            <TableListCell><p className={`skeleton`}></p></TableListCell>
                        </TableListRow>
                        <TableListRow>
                            <TableListCell><p className={`skeleton`}></p></TableListCell>
                            <TableListCell><p className={`skeleton`}></p></TableListCell>
                        </TableListRow>
                    </TableList>
                </Section>
            </div>
        )
    }

    // If loaded and no custom inputs are defined, return null
    if (!product || !product.customInputs || Object.keys(product.customInputs).length === 0) {
        return null
    }

    // Else return the custom inputs.
    return (
        <>
        {requiresAttention && <Callout type={`warning`}>{t('descriptionProductCustomDataMissing')}</Callout>}
            <div className={`content-inner-two-columns`}>
                {categoryIds?.map((categoryId) => {
            const category = allCategories.find(c => c.id === categoryId)
                    if (
                        !category ||
                        !category.customInputs?.customInputGroupOrder ||
                        !category.customInputs?.customInputGroups
                    ) return null
                    return (
                        <div key={categoryId} className={`content-inner`}>
                            {category.customInputs.customInputGroupOrder.map((customInputGroupId: string) => {
                                const customInputGroup = category.customInputs?.customInputGroups ? category.customInputs.customInputGroups[customInputGroupId] : undefined
                                
                                let productHasCustomInputs = false
                                if (customInputs && customInputs[categoryId]) {
                                    productHasCustomInputs = Object.keys(customInputs[categoryId]).length > 0
                                }
                                // If the custom input group does not exist, skip it.
                                if (!customInputGroup || !productHasCustomInputs) return null
                                
                                if (!customInputGroup || customInputGroup.customInputOrder.length === 0) return null
                                return (
                                    <Section key={customInputGroupId}>
                                        <SectionHeader>
                                            <SectionHeaderTitle>
                                                <h2>{customInputGroupId}</h2>
                                            </SectionHeaderTitle>
                                        </SectionHeader>
                                        <TableList key={customInputGroupId} width="fit">
                                                {customInputGroup.customInputOrder?.map((customInputId: string) => {

                                                // Get the custom input (if it exists).
                                                const customInput = customInputGroup.customInputs[customInputId]
                                                // Check if the custom input exists.
                                                if (!customInput) return null

                                                // Check if the value exists.
                                                const valueExists = customInputs && customInputs[categoryId] && customInputs[categoryId][customInputId] !== undefined

                                                // Get the value.
                                                const value = valueExists ? customInputs[categoryId][customInputId] : customInput.defaultValue

                                                // Return the data table row.
                                                if (!customInput.required && value === ``)
                                                    return null
                                                // Return the data table row.
                                                return (
                                                    <TableListRow key={customInputId} hoverEffect={false}>
                                                        <TableListCell isLabel={true}>{customInputId}</TableListCell>
                                                        <TableListCell>
                                                            {customInput.required && !valueExists ? <span className={`warning-test`}>{t('requiresAttention')}</span> : customInput.type === 'checkbox' ? value ? <CheckBoxIconFilled /> : <CheckBoxIconEmpty /> : value}
                                                        </TableListCell>
                                                    </TableListRow>
                                                )
                                                
                                            })}
                                        </TableList>
                                    </Section>
                                )
                            })}
                        </div>
                    ) 
                })}
            </div>
        </>
    )
}

export default CustomInputDataDisplay

